import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { FileArrowDown, Paperclip, X } from '@phosphor-icons/react';
import { API } from '../lib/api';
import { useSEO } from '../lib/seo';
import { useAuthUser } from '../lib/useAuth';
import { DashboardShell, EmptyState, Panel, StatCard, StatusPill, formatDate, inputCls } from '../components/dashboard/Bits';

const emptyQuote = { amount: '', currency: 'SGD', lead_time: '', validity: '', inclusions: '', exclusions: '', notes: '' };
const currencies = ['SGD', 'USD', 'EUR', 'GBP', 'MYR', 'AED', 'AUD', 'CAD', 'INR', 'ZAR'];

const ProviderDashboard = () => {
  useSEO({ title: 'Provider Dashboard | Vox Meridian', description: '', path: '/dashboard/provider', robots: 'noindex, nofollow' });
  const auth = useAuthUser();
  const [provider, setProvider] = useState(null);
  const [rfqs, setRfqs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [quoteFor, setQuoteFor] = useState(null);
  const [quote, setQuote] = useState(emptyQuote);
  const [file, setFile] = useState(null);
  const [acting, setActing] = useState(false);
  const [profileForm, setProfileForm] = useState({ tagline: '', about: '', certifications: '', website: '' });
  const [profilePhoto, setProfilePhoto] = useState(null);

  const load = useCallback(async () => {
    if (auth.status !== 'signed-in') return;
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/provider/rfqs`, { withCredentials: true });
      setProvider(data.provider);
      setRfqs(data.rfqs || []);
      const prof = data.provider?.profile || {};
      setProfileForm({
        tagline: prof.tagline || '',
        about: prof.about || '',
        certifications: (prof.certifications || []).join(', '),
        website: prof.website || '',
      });
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not load provider dashboard.');
    } finally {
      setLoading(false);
    }
  }, [auth.status]);

  useEffect(() => { load(); }, [load]);

  const saveProfile = async (e) => {
    e.preventDefault();
    setActing(true);
    try {
      await axios.put(`${API}/provider/profile`, {
        tagline: profileForm.tagline,
        about: profileForm.about,
        certifications: profileForm.certifications.split(',').map((s) => s.trim()).filter(Boolean),
        website: profileForm.website,
      }, { withCredentials: true });
      if (profilePhoto) {
        const fd = new FormData();
        fd.append('file', profilePhoto);
        await axios.post(`${API}/provider/profile/photo`, fd, {
          withCredentials: true,
          headers: { 'Content-Type': 'multipart/form-data' },
        });
      }
      toast.success('Public profile sent to the founder for review.');
      setProfilePhoto(null);
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not save profile.');
    } finally {
      setActing(false);
    }
  };

  const interest = async (rfqId) => {
    setActing(true);
    try {
      const { data } = await axios.post(`${API}/provider/rfqs/${rfqId}/interest`, {}, { withCredentials: true });
      toast.success(data.status === 'approved' ? 'Full RFQ access unlocked.' : 'Interest sent to the founder for review.');
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not register interest.');
    } finally {
      setActing(false);
    }
  };

  const submitQuote = async (e) => {
    e.preventDefault();
    if (!quoteFor) return;
    setActing(true);
    try {
      const fd = new FormData();
      Object.entries(quote).forEach(([k, v]) => fd.append(k, v));
      if (file) fd.append('file', file);
      await axios.post(`${API}/provider/rfqs/${quoteFor.id}/quotes`, fd, {
        withCredentials: true,
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      toast.success('Quote submitted to the buyer.');
      setQuoteFor(null);
      setQuote(emptyQuote);
      setFile(null);
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Quote submission failed.');
    } finally {
      setActing(false);
    }
  };

  if (auth.status === 'checking') return <div className="flex min-h-[70vh] items-center justify-center bg-ink" data-testid="provider-dashboard-loading"><p className="mono-label text-steel">Checking your session…</p></div>;
  if (auth.status === 'signed-out') {
    return <DashboardShell eyebrow="Provider dashboard" title={<>Sign in first.</>} sub="Matched RFQs and quote tracking are private to your provider account."><EmptyState testId="provider-signed-out" title="Google sign-in required." text="Sign in with the same email used on your provider application." actionLabel="Go to sign in" actionTo="/account" /></DashboardShell>;
  }

  if (!loading && !provider) {
    return (
      <DashboardShell eyebrow="Provider dashboard" title={<>No provider profile yet.</>} sub="This Google account is not linked to a provider application.">
        <EmptyState testId="provider-empty-profile" title="Apply as a provider first." text="Submit the public provider application with this same email. After founder verification, matched RFQs will appear here." actionLabel="Join as provider" actionTo="/providers" />
      </DashboardShell>
    );
  }

  const won = rfqs.reduce((sum, r) => sum + (r.my_quotes || []).filter((q) => q.status === 'accepted').length, 0);
  const submitted = rfqs.reduce((sum, r) => sum + (r.my_quotes || []).length, 0);

  return (
    <DashboardShell
      eyebrow="Provider dashboard"
      title={<>Matched RFQs. Clean quote flow.</>}
      sub="You see only RFQs matching your services, markets served and delivery modes. Full buyer details unlock when matched or approved."
    >
      {provider && (
        <Panel testId="provider-profile-panel" className="mb-8">
          <div className="flex flex-wrap items-start justify-between gap-4">
            <div>
              <p className="mono-label text-volt">Provider profile</p>
              <h2 className="mt-2 font-display text-2xl font-black text-parchment">{provider.company_name}</h2>
              <p className="mt-2 text-sm text-steel">{provider.service_country_names?.join(' · ')}</p>
            </div>
            <StatusPill status={provider.verification_status} />
          </div>
          <div className="mt-5 flex flex-wrap gap-2">
            {(provider.categories || []).map((cat) => <span key={cat} className="border border-line px-3 py-1.5 text-xs text-steel">{cat}</span>)}
          </div>
        </Panel>
      )}

      {provider?.verification_status === 'verified' && (
        <Panel title="Public directory profile" testId="provider-public-profile-panel" className="mb-8">
          <div className="mb-5 flex flex-wrap items-center gap-3">
            <StatusPill status={provider.profile_status || 'not published'} />
            {provider.profile_status === 'approved' && provider.profile_slug && (
              <a
                href={`/directory/${provider.profile_slug}`}
                data-testid="profile-live-link"
                className="font-mono text-[11px] font-bold uppercase tracking-[0.15em] text-volt hover:underline"
              >
                View your live profile →
              </a>
            )}
          </div>
          <p className="mb-5 text-sm leading-relaxed text-steel">
            Your public profile appears in the Vox Meridian directory once the founder approves it. Buyers see your
            services, markets, certifications and contact details — and reach you directly.
          </p>
          <form onSubmit={saveProfile} className="grid gap-4" data-testid="provider-public-profile-form">
            <div>
              <label className="mono-label mb-2 block text-steel">Tagline (max 140 chars)</label>
              <input
                className={inputCls}
                maxLength={140}
                value={profileForm.tagline}
                onChange={(e) => setProfileForm({ ...profileForm, tagline: e.target.value })}
                placeholder="24/7 hydraulic hose replacement across Johor & Singapore"
                data-testid="profile-tagline-input"
              />
            </div>
            <div>
              <label className="mono-label mb-2 block text-steel">About your company</label>
              <textarea
                rows={5}
                maxLength={2000}
                className={`${inputCls} resize-y`}
                value={profileForm.about}
                onChange={(e) => setProfileForm({ ...profileForm, about: e.target.value })}
                placeholder="Years in business, the work you take on, notable projects, team size…"
                data-testid="profile-about-textarea"
              />
            </div>
            <div className="grid gap-4 sm:grid-cols-2">
              <div>
                <label className="mono-label mb-2 block text-steel">Certifications (comma-separated)</label>
                <input
                  className={inputCls}
                  value={profileForm.certifications}
                  onChange={(e) => setProfileForm({ ...profileForm, certifications: e.target.value })}
                  placeholder="ISO 9001, bizSAFE Level 3, ASME IX"
                  data-testid="profile-certs-input"
                />
              </div>
              <div>
                <label className="mono-label mb-2 block text-steel">Website</label>
                <input
                  className={inputCls}
                  value={profileForm.website}
                  onChange={(e) => setProfileForm({ ...profileForm, website: e.target.value })}
                  placeholder="company.com"
                  data-testid="profile-website-input"
                />
              </div>
            </div>
            <div>
              <input
                id="profile-photo"
                type="file"
                className="hidden"
                accept=".png,.jpg,.jpeg,.webp"
                onChange={(e) => setProfilePhoto(e.target.files?.[0] || null)}
              />
              <label
                htmlFor="profile-photo"
                data-testid="profile-photo-dropzone"
                className="flex cursor-pointer items-center justify-center gap-2 border border-dashed border-line px-4 py-5 text-sm text-steel hover:border-volt hover:text-parchment"
              >
                <Paperclip size={15} /> {profilePhoto ? profilePhoto.name : 'Upload your company logo (PNG/JPG/WEBP, max 5MB)'}
              </label>
            </div>
            <button
              type="submit"
              disabled={acting}
              data-testid="profile-save-button"
              className="bg-volt px-5 py-3.5 font-mono text-xs font-bold uppercase tracking-[0.15em] text-ink transition-transform duration-200 hover:-translate-y-0.5 disabled:opacity-50"
            >
              {acting ? 'Saving…' : provider.profile_status === 'approved' ? 'Save changes — re-submit for review' : 'Submit profile for review'}
            </button>
          </form>
        </Panel>
      )}

      <div className="mb-8 grid gap-4 sm:grid-cols-3" data-testid="provider-dashboard-stats">
        <StatCard label="Matched RFQs" value={rfqs.length} testId="provider-stat-matched" />
        <StatCard label="Quotes submitted" value={submitted} testId="provider-stat-quotes" />
        <StatCard label="Jobs won" value={won} testId="provider-stat-won" />
      </div>

      {provider?.verification_status === 'founder_review' ? (
        <EmptyState testId="provider-review-pending" title="Application under founder review." text="Your profile is saved. Matched RFQs will appear after the founder verifies your application." />
      ) : rfqs.length === 0 ? (
        <EmptyState testId="provider-empty-rfqs" title="No matched RFQs right now." text="When a buyer RFQ matches your services, markets and delivery modes, it will appear here." />
      ) : (
        <div className="grid gap-5" data-testid="provider-rfq-list">
          {rfqs.map((rfq) => (
            <Panel key={rfq.id} testId={`provider-rfq-${rfq.id}`}>
              <div className="grid gap-6 lg:grid-cols-12">
                <div className="lg:col-span-8">
                  <div className="flex flex-wrap items-center gap-3">
                    <StatusPill status={rfq.status} />
                    <span className="font-mono text-[11px] uppercase tracking-[0.15em] text-steel">{rfq.country_name} · {(rfq.delivery_mode || 'on_site').replace(/_/g, ' ')}</span>
                    {rfq.cross_border && <span className="border border-volt/40 px-2 py-1 font-mono text-[10px] uppercase tracking-[0.15em] text-volt">Cross-border</span>}
                  </div>
                  <h2 className="mt-4 font-display text-2xl font-black text-parchment">{rfq.category}</h2>
                  <p className="mt-3 whitespace-pre-line text-sm leading-relaxed text-steel">{rfq.scope}</p>
                  <div className="mt-5 grid gap-3 sm:grid-cols-3">
                    <div><p className="mono-label text-steel">Budget</p><p className="mt-1 text-sm text-parchment">{rfq.budget_range}</p></div>
                    <div><p className="mono-label text-steel">Deadline</p><p className="mt-1 text-sm text-parchment">{formatDate(rfq.deadline)}</p></div>
                    <div><p className="mono-label text-steel">Access</p><p className="mt-1 text-sm text-parchment">{rfq.access === 'full' ? 'Full details' : 'Interest required'}</p></div>
                  </div>
                  {rfq.access === 'full' && (
                    <div className="mt-5 border border-line bg-ink p-4" data-testid={`provider-buyer-details-${rfq.id}`}>
                      <p className="mono-label text-steel">Buyer</p>
                      <p className="mt-2 text-sm text-parchment">{rfq.contact_name} — {rfq.company}</p>
                      <p className="mt-1 text-sm text-volt">{rfq.email}{rfq.phone ? ` · ${rfq.phone}` : ''}</p>
                      {rfq.file?.storage_path && (
                        <a href={`${API}/files/rfq/${rfq.id}`} data-testid={`provider-rfq-file-${rfq.id}`} className="mt-3 inline-flex items-center gap-1.5 font-mono text-[11px] uppercase tracking-[0.15em] text-volt">
                          <FileArrowDown size={14} /> {rfq.file.original_filename}
                        </a>
                      )}
                    </div>
                  )}
                </div>
                <div className="lg:col-span-4">
                  {(rfq.my_quotes || []).length > 0 ? (
                    <div className="border border-line bg-ink p-4" data-testid={`provider-my-quotes-${rfq.id}`}>
                      <p className="mono-label text-steel">Your quote</p>
                      {rfq.my_quotes.map((q) => (
                        <div key={q.id} className="mt-3 border-b border-line pb-3 last:border-b-0 last:pb-0">
                          <div className="flex items-center justify-between gap-2"><p className="text-sm text-parchment">{q.currency} {q.amount}</p><StatusPill status={q.status} /></div>
                          <p className="mt-1 text-xs text-steel">{q.lead_time}</p>
                        </div>
                      ))}
                    </div>
                  ) : rfq.access === 'full' ? (
                    <button type="button" onClick={() => { setQuoteFor(rfq); setQuote(emptyQuote); setFile(null); }} data-testid={`provider-quote-open-${rfq.id}`} className="w-full bg-volt px-5 py-3 font-mono text-xs font-bold uppercase tracking-[0.15em] text-ink transition-transform duration-200 hover:-translate-y-0.5">
                      Submit quote
                    </button>
                  ) : (
                    <button type="button" disabled={acting} onClick={() => interest(rfq.id)} data-testid={`provider-interest-${rfq.id}`} className="w-full border border-volt px-5 py-3 font-mono text-xs font-bold uppercase tracking-[0.15em] text-volt transition-colors duration-200 hover:bg-volt hover:text-ink disabled:opacity-50">
                      I’m interested
                    </button>
                  )}
                </div>
              </div>
            </Panel>
          ))}
        </div>
      )}

      {quoteFor && (
        <div className="fixed inset-0 z-50 overflow-y-auto bg-ink/90 p-4 backdrop-blur-sm" data-testid="provider-quote-modal">
          <div className="mx-auto my-10 max-w-3xl border border-line bg-surface p-6 sm:p-8">
            <div className="flex items-start justify-between gap-4">
              <div>
                <p className="mono-label text-volt">Submit quote</p>
                <h2 className="mt-2 font-display text-2xl font-black text-parchment">{quoteFor.category}</h2>
              </div>
              <button type="button" onClick={() => setQuoteFor(null)} data-testid="provider-quote-close" className="text-steel hover:text-parchment"><X size={20} /></button>
            </div>
            <form onSubmit={submitQuote} className="mt-6 space-y-5" data-testid="provider-quote-form">
              <div className="grid gap-5 sm:grid-cols-3">
                <div><label className="mono-label mb-2 block text-steel">Amount</label><input required className={inputCls} value={quote.amount} onChange={(e) => setQuote({ ...quote, amount: e.target.value })} data-testid="quote-amount-input" placeholder="4,800" /></div>
                <div><label className="mono-label mb-2 block text-steel">Currency</label><select className={inputCls} value={quote.currency} onChange={(e) => setQuote({ ...quote, currency: e.target.value })} data-testid="quote-currency-select">{currencies.map((c) => <option key={c} value={c}>{c}</option>)}</select></div>
                <div><label className="mono-label mb-2 block text-steel">Lead time</label><input required className={inputCls} value={quote.lead_time} onChange={(e) => setQuote({ ...quote, lead_time: e.target.value })} data-testid="quote-lead-time-input" placeholder="5 working days" /></div>
              </div>
              <div><label className="mono-label mb-2 block text-steel">Validity</label><input className={inputCls} value={quote.validity} onChange={(e) => setQuote({ ...quote, validity: e.target.value })} data-testid="quote-validity-input" placeholder="Valid for 14 days" /></div>
              <div><label className="mono-label mb-2 block text-steel">Included</label><textarea required rows={4} className={`${inputCls} resize-y`} value={quote.inclusions} onChange={(e) => setQuote({ ...quote, inclusions: e.target.value })} data-testid="quote-inclusions-textarea" /></div>
              <div><label className="mono-label mb-2 block text-steel">Excluded / assumptions</label><textarea rows={3} className={`${inputCls} resize-y`} value={quote.exclusions} onChange={(e) => setQuote({ ...quote, exclusions: e.target.value })} data-testid="quote-exclusions-textarea" /></div>
              <div><label className="mono-label mb-2 block text-steel">Notes</label><textarea rows={3} className={`${inputCls} resize-y`} value={quote.notes} onChange={(e) => setQuote({ ...quote, notes: e.target.value })} data-testid="quote-notes-textarea" /></div>
              <div>
                <input id="quote-file" type="file" className="hidden" accept=".pdf,.png,.jpg,.jpeg,.zip" onChange={(e) => setFile(e.target.files?.[0] || null)} />
                <label htmlFor="quote-file" data-testid="quote-file-dropzone" className="flex cursor-pointer items-center justify-center gap-2 border border-dashed border-line px-4 py-5 text-sm text-steel hover:border-volt hover:text-parchment">
                  <Paperclip size={15} /> {file ? file.name : 'Attach quote PDF (optional)'}
                </label>
              </div>
              <button type="submit" disabled={acting} data-testid="quote-submit-button" className="w-full bg-volt px-5 py-3.5 font-mono text-xs font-bold uppercase tracking-[0.15em] text-ink disabled:opacity-50">
                {acting ? 'Submitting…' : 'Submit quote'}
              </button>
            </form>
          </div>
        </div>
      )}
    </DashboardShell>
  );
};

export default ProviderDashboard;
